/**
 * Training feature (Sheet-2 row 9) — pure UI-side model helpers: persona predicates that
 * mirror training.controller.ts, DD-MM-YYYY formatting, quiz-draft validation that mirrors
 * `validateQuizSpecShape` (training/training.ts), and enrollment-history shaping.
 *
 * ISO/IEC 27002:2022 A.6.3 (Awareness, education and training) is the anchor control.
 * Dependency-free (no antd / MSAL) so it is unit-testable without mounting anything.
 *
 * Persona predicates are a UX affordance only — the API enforces every one of them
 * (training.controller.ts `isAdvisorOrAbove` / `isPublisher` / `LMS_CONFIG_ROLES`).
 */
import { Role } from '../constants/roles';

/** training.controller.ts ADVISOR_ROLES. platform_admin is deliberately excluded: admins do not author/approve/publish training. */
export function canAuthorTraining(role) {
    return role === Role.ADVISOR || role === Role.LEAD_ADVISOR;
}

/** training.controller.ts PUBLISH_ROLES ('lead_advisor') only; not platform_admin. */
export function canPublishTraining(role) {
    return role === Role.LEAD_ADVISOR;
}

/** training.controller.ts LMS_CONFIG_ROLES ('lead_advisor') only; not platform_admin. */
export function canConfigureLms(role) {
    return role === Role.LEAD_ADVISOR;
}

/** Learners see only published courses and their own enrollments. */
export function isTrainingLearner(role) {
    return role === Role.COMPANY_SPOC || role === Role.COMPANY_DELEGATE;
}

export const MODULE_TYPE_LABELS = Object.freeze({
    reading: 'Reading',
    quiz: 'Quiz',
    video: 'Video',
    live_session: 'Live session',
});

export const COURSE_STATUS_LABELS = Object.freeze({
    draft: 'Draft',
    published: 'Published',
    retired: 'Retired',
});

export const TRANSLATION_STATUS_LABELS = Object.freeze({
    pending_review: 'Pending review',
    approved: 'Approved',
    rejected: 'Rejected',
});

/** Indian-language targets for translation (Commandment XII), BCP-47 codes. English is the
 * source language and is never a translation target. */
export const TRAINING_LANGUAGES = Object.freeze([
    { value: 'hi', label: 'Hindi' },
    { value: 'ta', label: 'Tamil' },
    { value: 'mr', label: 'Marathi' },
    { value: 'bn', label: 'Bengali' },
    { value: 'gu', label: 'Gujarati' },
    { value: 'te', label: 'Telugu' },
    { value: 'kn', label: 'Kannada' },
    { value: 'ml', label: 'Malayalam' },
    { value: 'pa', label: 'Punjabi' },
]);

/** The API caps how many target languages one translate request may carry. */
export const MAX_LANGUAGES_PER_REQUEST = 5;

/** Backend default when a course is created without an explicit pass mark. */
export const DEFAULT_PASS_MARK_PERCENT = 70;

/** Backend default for a course's maximum quiz attempts (1-20). */
export const DEFAULT_MAX_ATTEMPTS = 5;

/** Attempts left for an enrolment, or null when the cap or count is not known. */
export function attemptsLeft(maxAttempts, attemptCount) {
    if (typeof maxAttempts !== 'number' || typeof attemptCount !== 'number') return null;
    return Math.max(maxAttempts - attemptCount, 0);
}

export function languageLabel(code) {
    if (code === 'en') return 'English';
    const hit = TRAINING_LANGUAGES.find((l) => l.value === code);
    return hit ? hit.label : code;
}

// Single shared formatter (Commandment XII) — see utils/formatDdMmYyyy.js.
export { formatDdMmYyyy } from './formatDdMmYyyy';

/** A module is an unreviewed AI draft until a human review event stamps `reviewedBy`. */
export function moduleNeedsReview(module) {
    return !!module && module.sourceType === 'ai_generated' && !module.reviewedBy;
}

export function courseHasAnyUnreviewedAiModule(course) {
    return Array.isArray(course?.modules) && course.modules.some(moduleNeedsReview);
}

/** Module-approve endpoint (the explicit human approval of an AI-drafted module). Kept in one
 * place so the path tracks the backend contract. */
export function moduleApprovePath(projectId, moduleId) {
    return `/projects/${projectId}/training/modules/${moduleId}/approve`;
}

/** Translations the human reviewer still has to decide on, across all modules. */
export function pendingTranslationCount(course) {
    if (!Array.isArray(course?.modules)) return 0;
    return course.modules.reduce(
        (n, m) => n + (m.translations || []).filter((t) => t.reviewStatus === 'pending_review').length,
        0,
    );
}

// ── Quiz drafting (mirrors backend validateQuizSpecShape) ─────────────────────────────

export function emptyQuizQuestion() {
    return { prompt: '', options: ['', ''], correctIndex: 0 };
}

/** @returns {string|null} first validation problem, or null when the draft is valid. */
export function validateQuizDraft(draft) {
    if (!draft || !Array.isArray(draft.questions) || draft.questions.length === 0) {
        return 'Add at least one question.';
    }
    for (let i = 0; i < draft.questions.length; i += 1) {
        const q = draft.questions[i];
        if (!q || typeof q.prompt !== 'string' || !q.prompt.trim()) return `Question ${i + 1} needs a prompt.`;
        if (!Array.isArray(q.options) || q.options.length < 2) return `Question ${i + 1} needs at least 2 options.`;
        if (q.options.some((o) => typeof o !== 'string' || !o.trim())) return `Question ${i + 1} has an empty option.`;
        if (!Number.isInteger(q.correctIndex) || q.correctIndex < 0 || q.correctIndex >= q.options.length) {
            return `Question ${i + 1} needs a correct answer selected.`;
        }
    }
    return null;
}

/** Normalises a draft into exactly the shape the API stores (trims text). */
export function quizDraftToPayload(draft) {
    return {
        questions: draft.questions.map((q) => ({
            prompt: q.prompt.trim(),
            options: q.options.map((o) => o.trim()),
            correctIndex: q.correctIndex,
        })),
    };
}

/** True when a learner has answered every question (all indices chosen). */
export function quizFullyAnswered(questionCount, answers) {
    if (!Array.isArray(answers) || answers.length < questionCount) return false;
    for (let i = 0; i < questionCount; i += 1) {
        if (!Number.isInteger(answers[i]) || answers[i] < 0) return false;
    }
    return true;
}

// ── Enrollment history ────────────────────────────────────────────────────────────────

/** Pass mark as the API reports it (percentage), or null when it is not reported — never guessed. */
export function passMarkOf(result, enrollment) {
    const candidates = [
        result?.passMarkPercent,
        result?.enrollment?.score?.passMarkPercent,
        enrollment?.score?.passMarkPercent,
    ];
    const hit = candidates.find((c) => typeof c === 'number' && !Number.isNaN(c));
    return hit === undefined ? null : hit;
}

export function formatScore(score) {
    if (!score || typeof score.totalCount !== 'number') return '-';
    return `${score.correctCount}/${score.totalCount} (${score.percentage}%)`;
}

const SOURCE_LABELS = { in_app: 'In app', lms_webhook: 'LMS' };
export function sourceLabel(source) {
    return SOURCE_LABELS[source] || source || '-';
}

/**
 * Joins enrollments to course titles and member names for the audit-evidence history table.
 * Learner names come from the project-members payload; an unknown id is shown as-is rather
 * than guessed at.
 */
export function buildEnrollmentRows(enrollments, courses, members) {
    const courseById = new Map((courses || []).map((c) => [c.id, c]));
    const memberById = new Map((members || []).map((m) => [m.id, m]));
    return (enrollments || []).map((e) => {
        const course = courseById.get(e.courseId);
        const member = memberById.get(e.userId);
        return {
            id: e.id,
            courseId: e.courseId,
            courseTitle: e.courseTitle || (course ? course.title : 'Unknown course'),
            courseVersion: e.courseVersion ?? course?.version,
            learnerId: e.userId,
            learnerName: member ? member.name : e.userId,
            learnerEmail: member ? member.email : null,
            enrolledAt: e.enrolledAt,
            completedAt: e.completedAt,
            completed: !!e.completedAt,
            certificateIssue: e.certificateIssue || null,
            attempted: !!e.score || (typeof e.attemptCount === 'number' && e.attemptCount > 0),
            score: e.score,
            hasCertificate: !!e.hasCertificate,
            attemptCount: typeof e.attemptCount === 'number' ? e.attemptCount : null,
            maxAttempts: typeof course?.maxAttempts === 'number' ? course.maxAttempts : null,
            source: e.source,
        };
    });
}

// ── Scoring across every quiz module ──────────────────────────────────────────────────

/** The API scores ALL quiz modules of a course as one submission: exactly one answer per
 * question, concatenated in module order. Returns each quiz module with its answer offset. */
export function quizSections(modules) {
    let offset = 0;
    return (modules || [])
        .filter((m) => m.moduleType === 'quiz')
        .map((m) => {
            const count = Array.isArray(m.contentJson?.questions) ? m.contentJson.questions.length : 0;
            const section = { module: m, offset, count };
            offset += count;
            return section;
        });
}

export function totalQuizQuestions(modules) {
    return quizSections(modules).reduce((n, s) => n + s.count, 0);
}

// ── Certificate issues ────────────────────────────────────────────────────────────────

/**
 * Plain explanation for a completed enrolment that has no certificate. `reason` is the API's
 * certificateReason / certificateIssue. NAME_UNRENDERABLE is NOT fixable by the UI: the name is
 * read from the learner's account and the platform never alters it, so the honest instruction is
 * to have the account name corrected and then retry (no name field is offered because nothing
 * could save it).
 */
export function certificateIssueText(reason, forOther = false) {
    const who = forOther ? "The learner's" : 'Your';
    switch (reason) {
        case 'NAME_UNRENDERABLE':
            return `${who} name on file contains characters that cannot be printed on a certificate. The name is never altered automatically: ask your administrator to correct it in the account, then retry.`;
        case 'STORAGE_NOT_CONFIGURED':
            return 'Certificate storage is not configured on this server, so the certificate could not be saved. Contact your administrator, then retry.';
        default:
            return 'The certificate could not be generated. You can retry.';
    }
}

/** Coverage entries the API marks selectable; only these are offered to a learner. */
export function selectableCoverage(coverage) {
    return (Array.isArray(coverage) ? coverage : []).filter((c) => c && c.selectable);
}

const ENGLISH_ONLY_LABELS = { video: 'video', live_session: 'live-session' };
export function englishOnlyNote(types) {
    const names = (types || []).map((t) => ENGLISH_ONLY_LABELS[t] || t);
    return names.length ? `${names.join(' and ')} module(s) stay in English.` : null;
}

export const TRANSLATIONS_LOCKED_MESSAGE = 'Translations of a published course cannot be changed. Create a new version to change translations.';
